import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useLanguage } from '../../../contexts/LanguageContext';

const TemplateEditorModal = ({ isOpen, onClose, template, currentLanguage, mode = 'edit' }) => {
  const { t } = useLanguage();
  const [templateName, setTemplateName] = useState(template?.name?.[currentLanguage] || '');
  const [templateLanguage, setTemplateLanguage] = useState(template?.language || 'both');
  const [headerEnabled, setHeaderEnabled] = useState(true);
  const [footerEnabled, setFooterEnabled] = useState(true);

  if (!isOpen || !template) return null;

  const languageOptions = [
    { value: 'fr', label: t('documents.french') },
    { value: 'ar', label: t('documents.arabic') },
    { value: 'both', label: t('documents.both') }
  ];

  const handleSave = () => {
    console.log('Saving template:', {
      id: template?.id,
      name: templateName,
      language: templateLanguage,
      headerEnabled,
      footerEnabled
    });
    
    alert(currentLanguage === 'fr' 
      ? `Modèle "${templateName}" enregistré avec succès` 
      : `تم حفظ النموذج "${templateName}" بنجاح`);
    
    onClose();
  };

  const isPreviewMode = mode === 'preview';

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" 
      onClick={onClose}
    >
      <div 
        className="bg-card rounded-lg border border-border shadow-warm max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e?.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${template?.bgColor} flex items-center justify-center`}>
              <Icon name={template?.icon} size={20} className={template?.color} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {isPreviewMode 
                  ? t('documents.previewTemplate')
                  : t('documents.editTemplate')}
              </h2>
              <p className="caption text-muted-foreground">{template?.name?.[currentLanguage]}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
            aria-label={t('documents.close')}
          />
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Settings Panel */}
            <div className="lg:col-span-1 space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-4">
                  {t('documents.templateSettings')}
                </h3>
                
                <div className="space-y-4">
                  <Input
                    label={t('documents.templateName')}
                    value={templateName}
                    onChange={(e) => setTemplateName(e?.target?.value)}
                    disabled={isPreviewMode}
                  />

                  <Select
                    label={t('documents.language')}
                    options={languageOptions}
                    value={templateLanguage}
                    onChange={setTemplateLanguage}
                    disabled={isPreviewMode}
                  />

                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Icon name="Image" size={18} className="text-muted-foreground" />
                        <span className="text-sm text-foreground">
                          {t('documents.header')}
                        </span>
                      </div>
                      <button
                        onClick={() => !isPreviewMode && setHeaderEnabled(!headerEnabled)}
                        disabled={isPreviewMode}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          headerEnabled ? 'bg-primary' : 'bg-muted'
                        } ${isPreviewMode ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            headerEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Icon name="FileText" size={18} className="text-muted-foreground" />
                        <span className="text-sm text-foreground">
                          {t('documents.footer')}
                        </span>
                      </div>
                      <button
                        onClick={() => !isPreviewMode && setFooterEnabled(!footerEnabled)}
                        disabled={isPreviewMode}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          footerEnabled ? 'bg-primary' : 'bg-muted'
                        } ${isPreviewMode ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            footerEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={18} className="text-accent flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-foreground">
                    <p className="font-medium mb-1">
                      {t('documents.usage')}
                    </p>
                    <p className="caption text-muted-foreground">
                      {template?.usageCount} {t('documents.documentsGenerated')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Preview Panel */}
            <div className="lg:col-span-2">
              <h3 className="text-sm font-semibold text-foreground mb-4">
                {t('documents.previewLabel')}
              </h3>
              
              <div className="bg-white border border-border rounded-lg p-8 min-h-[600px] shadow-inner">
                {/* Header */}
                {headerEnabled && (
                  <div className="border-b border-gray-300 pb-4 mb-6">
                    <div className="flex items-center justify-between">
                      <div className="w-16 h-16 bg-primary/10 rounded flex items-center justify-center">
                        <Icon name="Building2" size={32} className="text-primary" />
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-900">
                          {currentLanguage === 'fr' ? 'Agence Al-Baraka Travel' : 'وكالة البركة للسفر'}
                        </p>
                        <p className="text-xs text-gray-600">
                          {currentLanguage === 'fr' ? 'Casablanca, Maroc' : 'الدار البيضاء، المغرب'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Content */}
                <div className="space-y-4 mb-8">
                  <h1 className="text-2xl font-bold text-gray-900 text-center">
                    {template?.name?.[currentLanguage]}
                  </h1>
                  
                  <div className="space-y-3 text-gray-700">
                    <p className="text-sm">
                      {t('documents.templatePreview')}
                    </p>
                    
                    <div className="bg-gray-50 p-4 rounded border border-gray-200">
                      <p className="text-xs text-gray-600 mb-2">
                        {t('documents.availableVariables')}
                      </p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <span className="font-mono bg-white px-2 py-1 rounded">{'{{pilgrim_name}}'}</span>
                        <span className="font-mono bg-white px-2 py-1 rounded">{'{{group_name}}'}</span>
                        <span className="font-mono bg-white px-2 py-1 rounded">{'{{date}}'}</span>
                        <span className="font-mono bg-white px-2 py-1 rounded">{'{{agency_name}}'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                {footerEnabled && (
                  <div className="border-t border-gray-300 pt-4 mt-8">
                    <p className="text-xs text-gray-600 text-center">
                      {t('documents.autoGenerated')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('documents.close')}
          </Button>
          {!isPreviewMode && (
            <Button
              variant="default"
              iconName="Save"
              iconPosition="left"
              onClick={handleSave}
              disabled={!templateName}
            >
              {t('documents.save')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateEditorModal;